import React, { useEffect, useState } from "react";
import { getApplication } from "../../../services/user.services";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Loader from "../../admin/leads/Loader";
import currencies from "world-currencies";
import { formatDateTime } from "../../../utils/dateFormat";

// Format currency options
const CURRENCY_OPTIONS = Object.entries(currencies)
  .map(([code, currency]) => ({
    value: currency.iso.code,
    label: `${currency.name} (${currency.iso.code})`,
    symbol: currency.units?.major?.symbol || "N/A",
  }))
  .filter((currency) => currency.symbol);

// Better field display names with proper capitalization
const fieldDisplayNames = {
  intended_courses: "Intended Courses",
  scores: "Test Scores",
  comments: "Other Info",
  country_preference: "Preferred Country",
  university_choices: "University Choices",
  budget: "Budget",
  currency: "Currency",
  intakeMonth: "Intake Month",
  intakeYear: "Intake Year",
  intake: "Intake Date",
};

const getDisplayName = (key) =>
  fieldDisplayNames[key] ||
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Fields that should be excluded from rendering directly
const excludedFields = [
  "created_at",
  "student_id",
  "id",
  "intakeMonth",
  "intakeYear",
];

const formatMonthYearOnly = (date) => {
  console.log("Received date:", date);

  // Remove the ordinal suffixes (st, nd, rd, th) from the date
  const cleanedDate = date.replace(/(\d+)(st|nd|rd|th)/, "$1");

  const intakeDate = new Date(cleanedDate);

  if (isNaN(intakeDate)) {
    console.error("Invalid date:", cleanedDate);
    return "Invalid Date"; // Return fallback if invalid date
  }

  const options = { year: "numeric", month: "long" };
  return intakeDate.toLocaleDateString("en-US", options);
};

const StaticForm = () => {
  const { studentId } = useParams();
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const isSideBarOpen = useSelector((state) => state.application.isSideBarOpen);

  useEffect(() => {
    const fetchApplication = async () => {
      try {
        if (studentId) {
          const applicationData = await getApplication(studentId);

          // Process intake date if it exists
          if (applicationData.intake) {
            try {
              const intakeDate = new Date(applicationData.intake);
              const month = intakeDate.toLocaleString("en-US", {
                month: "long",
              });
              const year = intakeDate.getFullYear().toString();

              applicationData.intakeMonth = month;
              applicationData.intakeYear = year;
              applicationData.intake = formatDateTime(applicationData.intake);
            } catch (error) {
              console.error("Error parsing intake date:", error);
            }
          }

          // Format budget with currency if both exist
          if (applicationData.budget && applicationData.currency) {
            const currencyInfo = CURRENCY_OPTIONS.find(
              (opt) => opt.value === applicationData.currency
            );
            const symbol = currencyInfo
              ? currencyInfo.symbol
              : applicationData.currency;
            applicationData.formattedBudget = `${symbol} ${applicationData.budget}`;
          }

          setFormData(applicationData);
        }
      } catch (error) {
        console.error("Error fetching application:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchApplication();
  }, [studentId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader />
      </div>
    );
  }

  // Custom rendering for specific fields
  const renderFieldValue = (key, value, studentId) => {
    if (!value || value === "")
      return <span className="text-gray-400 italic">Not Provided</span>;

    switch (key) {
      case "budget":
        if (formData.formattedBudget) {
          return formData.formattedBudget;
        }
        return value;
      case "intake":
        return formatMonthYearOnly(value); // Use the updated date formatting function
      default:
        return value;
    }
  };

  // Order fields in a specific way
  const orderedFields = [
    "intended_courses",
    "intake",
    "scores",
    "country_preference",
    "budget",
    "university_choices",
    "comments",
  ];

  // Filter fields that should be shown
  const displayFields = Object.keys(formData)
    .filter((key) => !excludedFields.includes(key) && key !== "formattedBudget")
    .sort((a, b) => {
      const indexA = orderedFields.indexOf(a);
      const indexB = orderedFields.indexOf(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

  return (
    // <div
    //   className={`flex-1 bg-gray-50 ${
    //     isSideBarOpen ? "ml-80" : "ml-20"
    //   } min-h-screen p-6`}
    // >
    <div className="w-full mx-auto">
      <div className="space-y-6 bg-white p-6 rounded-lg shadow-sm">
        <div className="border-b pb-4 mb-4">
          <h2 className="text-xl font-bold text-gray-800">
            Application Details
          </h2>
          <p className="text-gray-600 mt-1">Student application information</p>
        </div>

        {displayFields.map((key) => (
          <div
            key={key}
            className="grid md:grid-cols-3 gap-4 items-start py-3 border-b border-gray-100 last:border-0"
          >
            <label className="font-medium text-gray-700">
              {getDisplayName(key)}
            </label>
            <div className="md:col-span-2">
              <div className="p-3 bg-gray-50 rounded-md">
                {renderFieldValue(key, formData[key], studentId)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
    // </div>
  );
};

export default StaticForm;
